import React, { useEffect, useRef, useState } from 'react';
import GaugeComponent from 'react-gauge-component';
import { useTranslation } from 'react-i18next';

import config from '../../../../config/config';
import { useBranding } from '../../../../contexts/BrandingContext';
import {
  ConnectorProperties,
  DeviceDetailsProperties,
} from '../../../../interface/device-management.interface';
import { useAppSelector } from '../../../../redux/hooks';
import getFirstSubdomain from '../../../../shared-functions/DomainsplitFunction';
import webSocketService from '../../../../websocket/wsService';

interface ConnectorStatusProperties {
  selectedDeviceGeneral: DeviceDetailsProperties;
}

/**
 * Gauge meter
 * @returns
 */
export default function ConnectorStatus(properties: ConnectorStatusProperties) {
  const { t } = useTranslation('devices');
  const { brandData } = useBranding();
  const domain = getFirstSubdomain();
  const { selectedDeviceGeneral } = properties;
  const previousSnReference = useRef<string | null>(null);
  const { currentMessage } = useAppSelector((state) => state.ocppMessage);

  const [availableConnectors, setAvailableConnectors] = useState<ConnectorProperties[]>();
  const [maxPowerOutput, setMaxPowerOutput] = useState(0);

  useEffect(() => {
    if (selectedDeviceGeneral) {
      const power = Number(selectedDeviceGeneral.maximumOutputPower || 0);
      setMaxPowerOutput(power);
      setAvailableConnectors(selectedDeviceGeneral.connectors || []);
    }
  }, [selectedDeviceGeneral]);

  useEffect(() => {
    const chargingPointSn = selectedDeviceGeneral?.chargingPointSn;
    const previousSn = previousSnReference.current;

    if (!chargingPointSn || previousSn === chargingPointSn) {
      return undefined; // ✅ explicitly return undefined for consistency
    }

    previousSnReference.current = chargingPointSn;
    const socketUrl = `${domain && brandData?.webSocketUrl ? brandData?.webSocketUrl : config.websocketUrl}/admin/${chargingPointSn}`;
    webSocketService.connect(socketUrl);

    return () => {
      webSocketService.disconnect();
    };
  }, [selectedDeviceGeneral?.chargingPointSn]);

  useEffect(() => {
    if (currentMessage?.connectorId) {
      setAvailableConnectors((previous) =>
        previous?.map((connector) =>
          connector.connectorId === currentMessage?.connectorId
            ? {
              ...connector,
              status: currentMessage?.status,
              chargingSpeed: currentMessage.chargingSpeed || 0,
            }
            : connector,
        ),
      );
    }
  }, [currentMessage]);

  return (
    <div className="connector-status-wrap">
      {availableConnectors?.map((connector, index) => (
        <div className="connector-status-container" key={index}>
          <GaugeComponent
            arc={{
              subArcs: [
                {
                  limit: maxPowerOutput / 2,
                  color: '#00A79D',
                  showTick: true,
                },
                {
                  limit: maxPowerOutput * (2 / 3),
                  color: '#C3C300',
                  showTick: true,
                },
                {
                  limit: maxPowerOutput,
                  color: '#DD1616',
                  showTick: true,
                },
              ],
            }}
            value={connector?.chargingSpeed || 0} // assuming this exists
            minValue={0}
            maxValue={maxPowerOutput}
          />
          <div className="connector-label">{t('connector')} {index + 1}</div>
        </div>
      ))}
    </div>
  );
}
